import React, { useEffect } from 'react';
import {
  Route,
  Redirect,
  RouteProps,
  RouteComponentProps
} from 'react-router-dom';
// eslint-disable-next-line custom-rules/no-react-i18next
import { useTranslation } from 'react-i18next';
import { useNotification } from '@invision/core';
import { useAuthenticationFlow } from '../../contexts';
import { AUTHENTICATION_ROUTES } from '../../constants';

interface ProtectedRouteProps extends RouteProps {
  /** Route requires authentication (valid session + systemId) */
  requiresAuth?: boolean;
  /** Custom redirect path when access is denied */
  redirectTo?: string;
  /** Show error notification when access is denied */
  showErrorNotification?: boolean;
}

/**
 * ProtectedRoute Component
 *
 * Provides declarative route-level access control for authentication routes.
 * Requires valid session (sessionId + systemId) for protected routes.
 *
 * Usage:
 * ```tsx
 * // Requires valid session
 * <ProtectedRoute path="/secure-page" requiresAuth component={SecurePage} />
 *
 * // Public route (no protection)
 * <ProtectedRoute path="/login" component={LoginPage} />
 * ```
 */
/**
 * Access Validation Component
 * Handles error notifications and redirects for denied access
 */
function AccessDenied({
  reason,
  redirectTo,
  showError
}: {
  reason: string;
  redirectTo: string;
  showError: boolean;
}) {
  const { error } = useNotification();

  useEffect(() => {
    if (showError) {
      error(reason);
    }
  }, [error, reason, showError]);

  return <Redirect to={redirectTo} />;
}

export function ProtectedRoute({
  requiresAuth = false,
  redirectTo = AUTHENTICATION_ROUTES.LOGIN,
  showErrorNotification = true,
  component: Component,
  render,
  children,
  ...routeProps
}: ProtectedRouteProps) {
  const { t } = useTranslation();
  const { sessionId, systemId } = useAuthenticationFlow();

  // Helper function to render the component/render/children
  function renderContent(props: RouteComponentProps): React.ReactNode {
    if (Component) {
      return <Component {...props} />;
    }
    if (render) {
      return render(props);
    }
    return typeof children === 'function' ? children(props) : children;
  }

  return (
    <Route
      {...routeProps}
      render={(props): React.ReactNode => {
        // Check if route requires authentication
        if (requiresAuth) {
          const hasValidSession = !!(sessionId && systemId);

          // Allow access if user has valid session
          if (hasValidSession) {
            return renderContent(props);
          }

          // Access denied - redirect to login
          return (
            <AccessDenied
              reason={t('Valid session required to access this page')}
              redirectTo={redirectTo}
              showError={showErrorNotification}
            />
          );
        }

        // Public route - render normally
        return renderContent(props);
      }}
    />
  );
}
